import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { ChartLine } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PerformanceChart() {
  const [data,    setData]    = useState([]);
  const [summary, setSummary] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [loading, setLoading] = useState(true);
  const svgRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/public/performance-chart`)
      .then(r => {
        setData(r.data.points || []);
        setSummary(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#71717A", fontSize: 13 }}>Cargando...</div>
    </div>
  );
  if (!data.length) return (
    <div style={{ padding: "36px 16px", textAlign: "center" }}>
      <ChartLine className="w-8 h-8 mx-auto mb-3" weight="fill" style={{ color: "rgba(212,175,55,0.4)" }} />
      <div style={{ fontSize: 13, fontWeight: 700, color: "#A1A1AA" }}>La curva se dibuja con los resultados en vivo</div>
      <div style={{ fontSize: 11, color: "#63636E", marginTop: 4 }}>Vuelve pronto para ver el profit acumulado pick a pick</div>
    </div>
  );

  // Chart dimensions: mas alto y dramatico
  const W = 900, H = 280, PAD = { top: 28, right: 30, bottom: 36, left: 12 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const profits = data.map(d => d.profit);
  const minP = Math.min(...profits);
  const maxP = Math.max(...profits);
  const rangeP = maxP - minP || 1;

  const xScale = i => PAD.left + (i / (data.length - 1)) * chartW;
  const yScale = v => PAD.top + chartH - ((v - minP) / rangeP) * chartH;

  // Build SVG path
  const pathD = data.map((d, i) => `${i === 0 ? "M" : "L"}${xScale(i).toFixed(1)},${yScale(d.profit).toFixed(1)}`).join(" ");
  // Area path (close at bottom)
  const areaD = pathD + ` L${xScale(data.length - 1).toFixed(1)},${(PAD.top + chartH).toFixed(1)} L${PAD.left},${(PAD.top + chartH).toFixed(1)} Z`;

  // Y axis ticks
  const yTicks = [minP, minP + rangeP * 0.25, minP + rangeP * 0.5, minP + rangeP * 0.75, maxP];

  // Zero line
  const zeroY = yScale(0);

  const finalProfit = summary?.final_profit ?? 0;
  const isPositive = finalProfit >= 0;

  return (
    <div style={{ width: "100%", marginTop: 12 }}>
      {/* HERO: tu dinero creciendo */}
      <div style={{
        textAlign: "center", marginBottom: 20, padding: "24px 16px 20px",
        background: `radial-gradient(120% 100% at 50% 0%, ${isPositive ? "rgba(16,185,129,0.14)" : "rgba(244,63,94,0.12)"} 0%, transparent 65%)`,
        borderRadius: 18,
        border: `1px solid ${isPositive ? "rgba(16,185,129,0.25)" : "rgba(244,63,94,0.25)"}`,
      }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.28em", color: "#8080A0", textTransform: "uppercase", marginBottom: 10 }}>
          Con 1 unidad por jugada
        </div>
        <div style={{
          fontFamily: "'Sora', sans-serif", fontWeight: 900,
          fontSize: "clamp(3.2rem, 14vw, 4.8rem)", lineHeight: 1,
          color: isPositive ? "#10B981" : "#F43F5E",
          filter: `drop-shadow(0 4px 24px ${isPositive ? "rgba(16,185,129,0.5)" : "rgba(244,63,94,0.5)"})`,
          letterSpacing: "-0.04em",
        }}>
          {finalProfit >= 0 ? "+" : ""}{finalProfit.toFixed(1)}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#B0B0C8", marginTop: 6 }}>
          unidades {isPositive ? "ganadas" : "en juego"} · {summary?.total_picks ?? 0} jugadas analizadas
        </div>
        {/* Barra visual: 100 -> 100+profit */}
        <div style={{ marginTop: 16, padding: "0 8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#707090", marginBottom: 6, fontWeight: 700 }}>
            <span>EMPIEZAS CON 100</span>
            <span>HOY TIENES {(100 + finalProfit).toFixed(1)}</span>
          </div>
          <div style={{ height: 14, borderRadius: 999, background: "rgba(255,255,255,0.06)", overflow: "hidden", position: "relative" }}>
            <div style={{
              height: "100%", borderRadius: 999,
              width: `${Math.min(100, Math.max(4, 50 + (finalProfit / 10) * 50))}%`,
              background: isPositive
                ? "linear-gradient(90deg, #0E9F6E, #10B981, #6EE7B7)"
                : "linear-gradient(90deg, #E11D48, #F43F5E)",
              boxShadow: isPositive ? "0 0 20px rgba(16,185,129,0.6)" : "0 0 20px rgba(244,63,94,0.6)",
              transition: "width 1s ease",
            }} />
          </div>
        </div>
      </div>

      {/* SVG Chart */}
      <div style={{ position: "relative", background: "#1A1A30", borderRadius: 12, border: "1px solid rgba(212,175,55,0.25)", overflow: "visible" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: "100%", display: "block", overflow: "visible" }}
          onMouseLeave={() => setHovered(null)}
        >
          <defs>
            {/* Area gradient — gold vibrante arriba, fade abajo */}
            <linearGradient id="pcGold" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#D4AF37" stopOpacity="0.55" />
              <stop offset="55%"  stopColor="#D4AF37" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.03" />
            </linearGradient>
            {/* Line glow filter */}
            <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            {/* Dot glow */}
            <filter id="dotGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.5" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Grid lines con labels DENTRO del chart area */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={yScale(t)} x2={W - PAD.right} y2={yScale(t)}
                stroke="rgba(255,255,255,0.07)" strokeWidth="1" strokeDasharray="4 6" />
              <text x={PAD.left + 8} y={yScale(t) - 6} textAnchor="start" fill="rgba(180,180,210,0.65)" fontSize="24" fontFamily="monospace" fontWeight="700">
                {t > 0 ? `+${t.toFixed(0)}u` : `${t.toFixed(0)}u`}
              </text>
            </g>
          ))}

          {/* Zero reference line */}
          {zeroY > PAD.top && zeroY < PAD.top + chartH && (
            <line x1={PAD.left} y1={zeroY} x2={W - PAD.right} y2={zeroY}
              stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" strokeDasharray="6 3" />
          )}

          {/* Area fill */}
          <path d={areaD} fill="url(#pcGold)" />

          {/* Glow copy of main line (blur only) */}
          <path d={pathD} fill="none" stroke="#D4AF37" strokeWidth="10" strokeLinejoin="round" strokeLinecap="round" strokeOpacity="0.16" />

          {/* Main line — gruesa y con glow */}
          <path d={pathD} fill="none" stroke="#D4AF37" strokeWidth="5" strokeLinejoin="round" strokeLinecap="round" filter="url(#lineGlow)" />

          {/* Dots con glow */}
          {data.map((d, i) => {
            const cx = xScale(i), cy = yScale(d.profit);
            if (d.result === "won")  return <circle key={i} cx={cx} cy={cy} r="4.5" fill="#10B981" stroke="#1A1A30" strokeWidth="2" filter="url(#dotGlow)" />;
            if (d.result === "lost") return <circle key={i} cx={cx} cy={cy} r="4.5" fill="#F43F5E" stroke="#1A1A30" strokeWidth="2" filter="url(#dotGlow)" />;
            return null;
          })}

          {/* Final profit callout — último punto destacado */}
          {data.length > 0 && (() => {
            const lastD = data[data.length - 1];
            const lx = xScale(data.length - 1);
            const ly = yScale(lastD.profit);
            const labelText = `${finalProfit >= 0 ? "+" : ""}${finalProfit.toFixed(1)}u`;
            return (
              <g>
                {/* Outer ring */}
                <circle cx={lx} cy={ly} r="9" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.4" />
                {/* Center dot */}
                <circle cx={lx} cy={ly} r="5" fill="#D4AF37" stroke="#1A1A30" strokeWidth="2" />
                {/* Value label */}
                <text x={lx} y={ly - 18} textAnchor="middle" fill="#D4AF37" fontSize="28" fontWeight="900" fontFamily="monospace"
                  filter="url(#lineGlow)">
                  {labelText}
                </text>
              </g>
            );
          })()}

          {/* Hover areas */}
          {data.map((d, i) => (
            <rect key={i}
              x={xScale(i) - (chartW / data.length / 2)}
              y={PAD.top}
              width={chartW / data.length}
              height={chartH}
              fill="transparent"
              onMouseEnter={() => setHovered({ ...d, cx: xScale(i), cy: yScale(d.profit) })}
            />
          ))}

          {/* Hover tooltip */}
          {hovered && (() => {
            const { cx, cy, profit, result, date, pick } = hovered;
            const isWon = result === "won";
            const color = isWon ? "#10B981" : result === "lost" ? "#F43F5E" : "#D4AF37";
            const tw = 120, th = 62;
            const tx = Math.min(Math.max(cx - tw / 2, PAD.left), W - PAD.right - tw);
            const ty = cy - th - 12 < PAD.top ? cy + 14 : cy - th - 12;
            return (
              <g>
                <line x1={cx} y1={PAD.top} x2={cx} y2={PAD.top + chartH} stroke={color} strokeWidth="1" strokeOpacity="0.35" strokeDasharray="3 3" />
                <circle cx={cx} cy={cy} r="7" fill={color} stroke="#1A1A30" strokeWidth="2.5" />
                <rect x={tx} y={ty} width={tw} height={th} rx="8" fill="#252545" stroke={`${color}55`} strokeWidth="1.5" />
                {date && <text x={tx + 10} y={ty + 16} fill="#9090AA" fontSize="9">{date}</text>}
                <text x={tx + 10} y={ty + 30} fill="#B0B0C8" fontSize="10">Pick #{pick}</text>
                <text x={tx + 10} y={ty + 48} fill={color} fontSize="15" fontWeight="900" fontFamily="monospace">
                  {profit >= 0 ? "+" : ""}{profit.toFixed(2)}u
                </text>
              </g>
            );
          })()}

          {/* X axis labels */}
          {[0, Math.floor(data.length * 0.25), Math.floor(data.length * 0.5), Math.floor(data.length * 0.75), data.length - 1].map(i => (
            <text key={i} x={xScale(i)} y={H - PAD.bottom + 26} textAnchor="middle" fill="rgba(180,180,210,0.65)" fontSize="24" fontFamily="monospace" fontWeight="700">
              {data[i]?.pick !== undefined ? `#${data[i].pick}` : `${i + 1}`}
            </text>
          ))}
        </svg>
      </div>

      <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 10, paddingBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", boxShadow: "0 0 6px rgba(16,185,129,0.7)" }} />
          <span style={{ color: "#B0B0C8", fontSize: 10, fontWeight: 700 }}>Ganada</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#F43F5E", boxShadow: "0 0 6px rgba(244,63,94,0.7)" }} />
          <span style={{ color: "#B0B0C8", fontSize: 10, fontWeight: 700 }}>Perdida</span>
        </div>
        <span style={{ color: "#505070", fontSize: 9, letterSpacing: "0.15em", fontWeight: 800, textTransform: "uppercase" }}>DATOS REALES DOCUMENTADOS</span>
      </div>
    </div>
  );
}
